import { Outlet, useOutletContext } from 'react-router'
/* Derived from logo-transparent.png, which stays in this folder as the master.
   Two things were wrong with using it directly:
   1. Size — 1024x1024 at 1.43MB, larger than the entire gzipped JS bundle, to
      fill a 44px slot.
   2. Padding — the master is a SQUARE canvas holding a wide wordmark. The
      artwork is 933x573 with 219px of transparent space above and 232px below,
      so it fills only 56% of the height. Any `h-*` sizes the canvas, not the
      mark, which made h-9 (36px) render as ~20px of visible logo. No CSS fixes
      that; the padding has to come off the asset.
   This copy is cropped to the opaque bounds and is 430x264 — cut for the
   sign-in page, which renders the mark 88px tall and therefore needs 3x of
   that. It is far more than this 44px slot requires; the header shares it
   anyway so the build carries one logo instead of two. At 44.1 KB that is
   8.8 KB more than the 215x132 copy it replaced, against 35.3 KB saved by not
   shipping both.

   Vite only emits imported assets, so the unimported master costs the build
   nothing. Re-derive from it rather than upscaling this. */
import logo from '@/assets/logo-wordmark-430.png'
import type { PoolSummary } from '@/features/pools/api'
import TabBar from './TabBar'

/* PoolSummary, not Tables<'pools'>. RequirePool populates this from
   fetchPoolBySlug's explicit column list, so typing it as the full row let
   `pool.tie_policy` compile while being undefined at runtime. */
export interface PoolContext {
  pool: PoolSummary
}

export default function AppShell() {
  const { pool } = useOutletContext<PoolContext>()

  return (
    <div className="bg-stock min-h-dvh">
      {/* relative + overflow-hidden so the yard-line overlay is clipped to the
          chrome and cannot bleed down behind the pick rows. */}
      <header className="bg-navy relative overflow-hidden">
        <div className="mx-auto flex max-w-md items-center justify-between px-5 py-3">
          {/* The transparent PNG, not the text wordmark — the old inline
              "SIDE ACTION" clipped at narrow widths. alt carries the brand so
              the logo is not announced as decoration. Intrinsic width/height are
              declared so the browser reserves 44x72 before the PNG loads instead
              of reflowing the header. */}
          <img src={logo} alt="Side Action" width={430} height={264} className="h-11 w-auto" />
        </div>
        <div className="bg-navy-2 mx-auto max-w-md px-5 py-2.5">
          <h2 className="text-team font-bold text-white">{pool.name}</h2>
        </div>
        <span aria-hidden className="yard-lines pointer-events-none absolute inset-0" />
      </header>

      <div className="mx-auto max-w-md pb-24">
        <Outlet context={{ pool }} />
      </div>

      <TabBar base={`/p/${pool.slug}`} />
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePool(): PoolSummary {
  return useOutletContext<PoolContext>().pool
}
